import React, { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query'
import { Moon, Loader2, Play, Square, ExternalLink, AlertTriangle, CheckCircle2, MinusCircle, Clock, ChevronDown, ChevronRight, Upload } from 'lucide-react'
import * as api from '../services/overviewsApi'

// Autopilot (doc/plan-overview-autopilot.md): plan an unattended run over a native
// series, start it for the night, and read the morning card — what was made, what
// was skipped and why, what it cost.

const input =
  'h-9 rounded-lg px-2.5 text-sm bg-white border border-parchment-border text-ink focus:outline-none focus:ring-2 focus:ring-gold/50 dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist'
const btn =
  'inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-sm border border-gold/50 text-gold-deep hover:bg-gold/10 disabled:opacity-40 disabled:cursor-not-allowed dark:text-gold'
const btnGhost =
  'inline-flex items-center gap-1.5 h-8 px-2.5 rounded-md text-xs text-ink-soft hover:bg-parchment-200 disabled:opacity-40 dark:text-mist/70 dark:hover:bg-midnight-700'
const card = 'rounded-xl border border-parchment-border bg-white/60 dark:border-midnight-600 dark:bg-midnight-800/50'
const label = 'text-[11px] uppercase tracking-wider text-ink-soft dark:text-mist/50'

const END_LABEL = {
  produce: 'Produce only (no upload)',
  private: 'Upload private, approve in the morning',
  schedule: 'Schedule releases',
  public: 'Publish',
}
const STEP_LABEL = {
  outline: 'outline', brief: 'brief', rebrief: 're-brief', research: 'Deep Research', 'research-index': 'add report',
  script: 'script', polish: 'polish', checks: 'checks', prompts: 'prompts', generate: 'generate', thumbnail: 'thumbnail', upload: 'upload',
}
const RUN_STATUS = {
  scheduled: ['Scheduled', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  running: ['Running', 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200'],
  waiting: ['Waiting for the next night', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'],
  done: ['Finished', 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'],
  stopped: ['Stopped', 'bg-parchment-200 text-ink-soft dark:bg-midnight-700 dark:text-mist/70'],
  budget: ['Budget spent', 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'],
  failed: ['Failed', 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'],
}
const ACTIVE = new Set(['scheduled', 'running', 'waiting'])

const usd = (v) => (v == null ? '—' : `$${Number(v).toFixed(2)}`)
const when = (iso) => (iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')
const hours = (min) => (min >= 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`)

export default function OverviewAutopilot({ series, onOpenEpisode, onChanged }) {
  const q = useQuery({
    queryKey: ['overviews', 'autopilot', series.id],
    queryFn: () => api.getAutopilot(series.id),
    refetchInterval: (query) => (query.state.data?.active ? 5000 : 30000),
  })
  const [planning, setPlanning] = useState(false)
  const [openRun, setOpenRun] = useState(null)
  const runs = q.data?.runs ?? []
  const active = runs.find((r) => ACTIVE.has(r.status))
  const latest = runs[0]

  useEffect(() => { if (active) onChanged?.() }, [active?.current?.step, active?.counts?.done, active?.counts?.skipped]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <section className={`${card} p-4 space-y-3`}>
      <div className="flex items-center gap-2">
        <Moon className="w-4 h-4 text-gold-deep dark:text-gold" />
        <h3 className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold flex-1">Autopilot</h3>
        {!active && !planning && (
          <button className={btn} onClick={() => setPlanning(true)}><Play className="w-4 h-4" /> Plan a night run</button>
        )}
      </div>
      {q.isError && <p className="text-sm text-red-600">{q.error.message}</p>}
      {active && <RunCard run={active} live onOpenEpisode={onOpenEpisode} onChanged={() => q.refetch()} />}
      {!active && planning && q.data && (
        <PlanForm series={series} defaults={q.data.defaults} onCancel={() => setPlanning(false)} onStarted={() => { setPlanning(false); q.refetch(); onChanged?.() }} />
      )}
      {!active && !planning && latest && <RunCard run={latest} onOpenEpisode={onOpenEpisode} onChanged={() => { q.refetch(); onChanged?.() }} />}
      {runs.length > 1 && (
        <div className="space-y-1">
          <p className={label}>Earlier runs</p>
          {runs.filter((r) => r.id !== (active ?? latest)?.id).map((r) => (
            <div key={r.id}>
              <button className="text-xs text-ink-soft dark:text-mist/60 inline-flex items-center gap-1" onClick={() => setOpenRun(openRun === r.id ? null : r.id)}>
                {openRun === r.id ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Run {r.id} · {RUN_STATUS[r.status]?.[0] ?? r.status} · {when(r.created_at)} · {r.counts.done} done, {r.counts.skipped} skipped · {usd(r.spent_usd)}
              </button>
              {openRun === r.id && <div className="mt-2"><RunCard run={r} onOpenEpisode={onOpenEpisode} onChanged={() => q.refetch()} /></div>}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}

function PlanForm({ series, defaults, onCancel, onStarted }) {
  const [o, setO] = useState(() => ({ ...defaults, research: { ...defaults.research }, cadence: { ...defaults.cadence }, exclude: [] }))
  const [debounced, setDebounced] = useState(o)
  useEffect(() => { const t = setTimeout(() => setDebounced(o), 600); return () => clearTimeout(t) }, [o])
  // The rows come from a plan without exclusions, so ticking an episode on or off
  // never reloads the table; the totals follow the ticks at once. A second preview
  // with the exclusions only checks what the server would refuse or warn about.
  const { exclude: _ex, ...rowOpts } = debounced
  const rowsQ = useQuery({
    queryKey: ['overviews', 'autopilot-preview', series.id, rowOpts],
    queryFn: () => api.previewAutopilot({ seriesId: series.id, options: { ...rowOpts, exclude: [] } }),
    staleTime: 15000,
    placeholderData: keepPreviousData,
  })
  const pv = useQuery({
    queryKey: ['overviews', 'autopilot-preview', series.id, debounced],
    queryFn: () => api.previewAutopilot({ seriesId: series.id, options: debounced }),
    staleTime: 15000,
    placeholderData: keepPreviousData,
  })
  const settled = !pv.isFetching && !pv.isPlaceholderData && debounced === o
  const start = useMutation({ mutationFn: () => api.startAutopilot({ seriesId: series.id, options: o }), onSuccess: onStarted })
  const set = (patch) => setO((x) => ({ ...x, ...patch }))
  const setR = (patch) => setO((x) => ({ ...x, research: { ...x.research, ...patch } }))
  const setC = (patch) => setO((x) => ({ ...x, cadence: { ...x.cadence, ...patch } }))
  const toggleEp = (id) => setO((x) => ({ ...x, exclude: x.exclude.includes(id) ? x.exclude.filter((i) => i !== id) : [...x.exclude, id] }))
  const rows = rowsQ.data?.episodes ?? []
  const todoIds = rows.filter((e) => e.steps.length).map((e) => e.episode_id)
  const setAll = (on) => setO((x) => ({ ...x, exclude: on ? [] : todoIds }))
  const included = rows.filter((e) => !o.exclude.includes(e.episode_id))
  const total = included.reduce((t, e) => ({
    usd: t.usd + (e.usd || 0), minutes: t.minutes + (e.minutes || 0), tts_chars: t.tts_chars + (e.tts_chars || 0),
    uploads: t.uploads + (e.steps.includes('upload') ? 1 : 0), count: t.count + (e.steps.length ? 1 : 0),
  }), { usd: 0, minutes: 0, tts_chars: 0, uploads: 0, count: 0 })
  const data = pv.data
  const errors = data?.preflight?.errors ?? []
  const warnings = data?.preflight?.warnings ?? []
  const uploads = series.channel === 'youtube' || series.channel === 'podcast'

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="space-y-1">
          <span className={label}>End of the run</span>
          <select className={`${input} w-full`} value={o.end} onChange={(e) => set({ end: e.target.value })}>
            {Object.entries(END_LABEL).filter(([k]) => uploads || k === 'produce').map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className={label}>Budget per night (USD)</span>
          <input className={`${input} w-full`} type="number" min="0" step="1" placeholder="no cap" value={o.budget_usd ?? ''} onChange={(e) => set({ budget_usd: e.target.value === '' ? null : Number(e.target.value) })} />
        </label>
        <div className="space-y-1">
          <span className={label}>Window</span>
          <div className="flex items-center gap-2">
            {o.start === 'now'
              ? <span className="text-sm text-ink dark:text-mist">now</span>
              : <input id="ap-start" className={input} type="time" value={o.start} onChange={(e) => set({ start: e.target.value })} />}
            <span className="text-xs text-ink-soft">to</span>
            {o.until == null
              ? <span className="text-sm text-ink dark:text-mist">done</span>
              : <input id="ap-until" className={input} type="time" value={o.until} onChange={(e) => set({ until: e.target.value })} />}
          </div>
          <div className="flex gap-3 text-[11px] text-ink-soft dark:text-mist/60">
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={o.start === 'now'} onChange={(e) => set({ start: e.target.checked ? 'now' : defaults.start === 'now' ? '22:00' : defaults.start })} /> start now</label>
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={o.until == null} onChange={(e) => set({ until: e.target.checked ? null : defaults.until || '06:00' })} /> no end</label>
          </div>
        </div>
        <label className="space-y-1">
          <span className={label}>Thumbnail</span>
          <select className={`${input} w-full`} value={o.thumbnail} onChange={(e) => set({ thumbnail: e.target.value })}>
            <option value="ai">AI image (≈ $0.05)</option>
            <option value="template">Cover template</option>
          </select>
        </label>
        <div className="space-y-1">
          <span className={label}>Deep Research on the gaps</span>
          <div className="flex items-center gap-2 text-sm text-ink dark:text-mist">
            <input className={`${input} w-16`} type="number" min="0" max="3" value={o.research.per_episode} onChange={(e) => setR({ per_episode: Number(e.target.value) })} />
            <span className="text-xs">per episode, at most</span>
            <input className={`${input} w-16`} type="number" min="0" max="50" value={o.research.max} onChange={(e) => setR({ max: Number(e.target.value) })} />
            <span className="text-xs">a run</span>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] text-ink-soft dark:text-mist/60">
            <label className="inline-flex items-center gap-1"><input type="checkbox" checked={o.research.deepdives} onChange={(e) => setR({ deepdives: e.target.checked })} /> deep dives too</label>
            {series.projects.length > 1 && (
              <label className="inline-flex items-center gap-1">report into
                <select className="h-6 rounded px-1 text-[11px] bg-white border border-parchment-border dark:bg-midnight-700 dark:border-midnight-600" value={o.research.project ?? ''} onChange={(e) => setR({ project: e.target.value })}>
                  {series.projects.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
            )}
          </div>
        </div>
        {o.end === 'schedule' && (
          <div className="space-y-1">
            <span className={label}>Release cadence</span>
            <div className="flex items-center gap-2 text-xs text-ink dark:text-mist">
              <input id="ap-first" className={input} type="date" value={o.cadence.first_date ?? ''} onChange={(e) => setC({ first_date: e.target.value || null })} title="First release (blank = tomorrow)" />
              <input id="ap-time" className={input} type="time" value={o.cadence.time} onChange={(e) => setC({ time: e.target.value })} />
            </div>
            <div className="flex items-center gap-2 text-xs text-ink-soft dark:text-mist/60">
              every <input className={`${input} w-16`} type="number" min="1" max="30" value={o.cadence.every_days} onChange={(e) => setC({ every_days: Number(e.target.value) })} /> day(s)
            </div>
          </div>
        )}
      </div>

      <div className="space-y-1">
        <div className="flex items-baseline justify-between gap-3">
          <p className={label}>Plan</p>
          <span className="flex items-center gap-2 text-[11px] text-ink-soft dark:text-mist/60">
            {(rowsQ.isFetching || pv.isFetching) && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {todoIds.length > 1 && <>
              <button type="button" className="underline disabled:no-underline disabled:opacity-40" disabled={!o.exclude.length} onClick={() => setAll(true)}>all</button>
              <button type="button" className="underline disabled:no-underline disabled:opacity-40" disabled={todoIds.every((id) => o.exclude.includes(id))} onClick={() => setAll(false)}>none</button>
            </>}
          </span>
        </div>
        {(rowsQ.isError || pv.isError) && <p className="text-sm text-red-600">{(rowsQ.error || pv.error).message}</p>}
        {rowsQ.data && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink-soft dark:text-mist/50">
                  <th className="py-1 pr-2 font-normal">Run</th><th className="py-1 pr-2 font-normal">#</th><th className="py-1 pr-2 font-normal">Episode</th>
                  <th className="py-1 pr-2 font-normal">Steps</th><th className="py-1 pr-2 font-normal text-right">≈ USD</th><th className="py-1 font-normal text-right">≈ min</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const off = o.exclude.includes(e.episode_id)
                  return (
                    <tr key={e.episode_id} className={`border-t border-parchment-border/60 dark:border-midnight-600/60 ${off ? 'opacity-40' : ''}`}>
                      <td className="py-1 pr-2"><input type="checkbox" className="w-4 h-4 accent-gold" checked={!off} disabled={!e.steps.length} onChange={() => toggleEp(e.episode_id)} aria-label={`Include ${e.label}`} /></td>
                      <td className="py-1 pr-2 text-ink-soft tabular-nums">{e.label}</td>
                      <td className={`py-1 pr-2 text-ink dark:text-mist ${e.kind === 'deepdive' ? 'pl-3' : ''}`}>{e.title}</td>
                      <td className="py-1 pr-2 text-ink-soft dark:text-mist/60">{off ? 'left out' : e.steps.length ? e.steps.map((s) => STEP_LABEL[s] ?? s).join(' → ') : e.note}</td>
                      <td className="py-1 pr-2 text-right tabular-nums">{off ? '' : usd(e.usd)}</td>
                      <td className="py-1 text-right tabular-nums">{off ? '' : e.minutes}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-ink dark:text-mist">
              {total.count} episode{total.count === 1 ? '' : 's'} · ≈ {usd(total.usd)} · ≈ {hours(total.minutes)} of steps one after another
              {total.uploads ? ` · ${total.uploads} upload${total.uploads > 1 ? 's' : ''}` : ''}
              {total.tts_chars ? ` · ≈ ${total.tts_chars.toLocaleString('cs-CZ')} TTS characters` : ''}
            </p>
            <p className="text-[11px] text-ink-soft dark:text-mist/50">Estimates from earlier runs; Deep Research is counted where gaps exist today, a re-brief can find more.</p>
          </div>
        )}
      </div>

      {!settled && (errors.length > 0 || warnings.length > 0) && <p className="text-[11px] text-ink-soft dark:text-mist/50">Checking the new selection…</p>}
      {errors.map((m) => <p key={m} className="text-sm text-red-600 flex gap-1.5"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{m}</p>)}
      {warnings.map((m) => <p key={m} className="text-xs text-amber-700 dark:text-amber-400 flex gap-1.5"><AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />{m}</p>)}
      {start.error && <p className="text-sm text-red-600">{start.error.message}</p>}
      <div className="flex items-center gap-2">
        <button className={btn} disabled={!data || !settled || errors.length > 0 || start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Moon className="w-4 h-4" />}
          {o.start === 'now' ? 'Run now' : `Run overnight from ${o.start}`}
        </button>
        <button className={btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

function RunCard({ run, live = false, onOpenEpisode, onChanged }) {
  const stop = useMutation({ mutationFn: () => api.stopAutopilot(run.id), onSuccess: onChanged })
  const upload = useMutation({ mutationFn: (id) => api.publishToChannel({ id, privacy: 'private' }), onSuccess: onChanged })
  const [showLog, setShowLog] = useState(false)
  const [statusText, statusCls] = RUN_STATUS[run.status] ?? [run.status, '']
  const o = run.options ?? {}
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-soft dark:text-mist/60">
        <span className={`px-2 py-0.5 rounded text-[11px] ${statusCls}`}>{statusText}</span>
        <span>Run {run.id}</span>
        <span>· {END_LABEL[o.end] ?? o.end}</span>
        <span>· spent {usd(run.spent_usd)}{o.budget_usd != null ? ` of ${usd(o.budget_usd)} a night` : ''}</span>
        {ACTIVE.has(run.status) && <span>· window {when(run.starts_at)}{run.until_at ? ` – ${when(run.until_at)}` : ''}</span>}
        {run.finished_at && <span>· finished {when(run.finished_at)}</span>}
        <span>· {run.counts.done} done, {run.counts.skipped} skipped{run.counts.pending ? `, ${run.counts.pending} to go` : ''}</span>
        {run.research_total > 0 && <span>· {run.research_total} Deep Research</span>}
        {live && (
          <button className={`${btnGhost} ml-auto`} disabled={stop.isPending} onClick={() => { if (confirm('Stop the run? The step that is running finishes on its own; nothing new starts.')) stop.mutate() }}>
            <Square className="w-3.5 h-3.5" /> Stop
          </button>
        )}
      </div>
      {run.current && (
        <p className="text-sm text-ink dark:text-mist inline-flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" /> #{run.current.label}: {STEP_LABEL[run.current.step] ?? run.current.step} since {when(run.current.since)}
        </p>
      )}
      {run.retry_at && <p className="text-xs text-amber-700 dark:text-amber-400">Retrying at {when(run.retry_at)}.</p>}
      {run.error && <p className="text-sm text-red-600">{run.error}</p>}
      {stop.error && <p className="text-sm text-red-600">{stop.error.message}</p>}
      {upload.error && <p className="text-sm text-red-600">{upload.error.message}</p>}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <tbody>
            {run.episodes.map((e) => (
              <tr key={e.episode_id} className="border-t border-parchment-border/60 dark:border-midnight-600/60 align-top">
                <td className="py-1.5 pr-2 w-5"><RowIcon status={e.status} current={!!e.current} /></td>
                <td className="py-1.5 pr-2 text-ink-soft tabular-nums whitespace-nowrap">{e.label}</td>
                <td className="py-1.5 pr-2 min-w-[12rem]">
                  <button className={`text-left text-ink dark:text-mist hover:underline ${e.kind === 'deepdive' ? 'pl-3' : ''}`} onClick={() => onOpenEpisode?.(e.episode_id)}>{e.title}</button>
                  {e.reason && <div className="text-red-700 dark:text-red-400">{e.reason}</div>}
                  {!e.reason && e.note && e.status === 'done' && <div className="text-ink-soft dark:text-mist/50">{e.note}</div>}
                </td>
                <td className="py-1.5 pr-2 text-ink-soft dark:text-mist/60">
                  {e.steps.map((s, i) => (
                    <span key={i} className={s.ok ? '' : 'text-red-700 dark:text-red-400'} title={s.error || `${s.minutes} min`}>{i ? ' → ' : ''}{STEP_LABEL[s.step] ?? s.step}{s.ok ? '' : ' ✗'}</span>
                  ))}
                  {e.current && <span className="text-cyan-700 dark:text-cyan-300">{e.steps.length ? ' → ' : ''}{STEP_LABEL[e.current] ?? e.current}…</span>}
                </td>
                <td className="py-1.5 pr-2 text-right tabular-nums whitespace-nowrap">{e.cost != null ? usd(e.cost) : ''}</td>
                <td className="py-1.5 text-right whitespace-nowrap">
                  {e.remote_id && e.url?.includes('youtube') && (
                    <a className="inline-flex items-center gap-1 text-gold-deep dark:text-gold" href={`https://studio.youtube.com/video/${e.remote_id}/edit`} target="_blank" rel="noreferrer" title={e.publish_at ? `release ${when(e.publish_at)}` : `privacy: ${e.privacy}`}>
                      Studio <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {!e.remote_id && e.status === 'done' && e.note === 'produced' && !ACTIVE.has(run.status) && (
                    <button className={btnGhost} disabled={upload.isPending} onClick={() => upload.mutate(e.episode_id)} title="Upload to YouTube as private">
                      <Upload className="w-3.5 h-3.5" /> Upload
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {run.log?.length > 0 && (
        <div>
          <button className="text-[11px] text-ink-soft dark:text-mist/50 inline-flex items-center gap-1" onClick={() => setShowLog(!showLog)}>
            {showLog ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />} Log
          </button>
          {showLog && (
            <ul className="mt-1 max-h-64 overflow-y-auto text-[11px] font-mono text-ink-soft dark:text-mist/60 space-y-0.5">
              {run.log.map((l, i) => <li key={i}>{when(l.at)} {l.label ? `#${l.label} ` : ''}{l.msg}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function RowIcon({ status, current }) {
  if (current || status === 'running') return <Loader2 className="w-3.5 h-3.5 animate-spin text-cyan-600" />
  if (status === 'done') return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
  if (status === 'skipped') return <MinusCircle className="w-3.5 h-3.5 text-red-600" />
  return <Clock className="w-3.5 h-3.5 text-ink-soft/60" />
}

export function PublishingCard({ series, onSaved }) {
  const [synthetic, setSynthetic] = useState(series.publishing?.synthetic !== false)
  const [disclaimer, setDisclaimer] = useState(series.publishing?.disclaimer ?? '')
  const [numbered, setNumbered] = useState(series.publishing?.numbered_titles !== false)
  const dirty = synthetic !== (series.publishing?.synthetic !== false) || disclaimer !== (series.publishing?.disclaimer ?? '') || numbered !== (series.publishing?.numbered_titles !== false)
  const save = useMutation({ mutationFn: () => api.patchSeries({ id: series.id, publishing: { synthetic, disclaimer, numbered_titles: numbered } }), onSuccess: onSaved })
  const [open, setOpen] = useState(false)
  const summary = useMemo(() => `${synthetic ? 'marked as synthetic content' : 'not marked as synthetic'}${disclaimer ? ' · notice at the end of each description' : ''}`, [synthetic, disclaimer])
  return (
    <section className={`${card} px-4 py-3 space-y-2`}>
      <button className="w-full flex items-center gap-2 text-left" onClick={() => setOpen(!open)}>
        {open ? <ChevronDown className="w-3.5 h-3.5 text-ink-soft" /> : <ChevronRight className="w-3.5 h-3.5 text-ink-soft" />}
        <span className="text-sm uppercase tracking-[0.18em] text-gold-deep dark:text-gold">Publishing</span>
        <span className="text-[11px] text-ink-soft dark:text-mist/50 truncate">{summary}</span>
      </button>
      {open && (
        <div className="space-y-2 pl-5">
          <label className="flex items-start gap-2 text-sm text-ink dark:text-mist">
            <input type="checkbox" className="mt-1" checked={synthetic} onChange={(e) => setSynthetic(e.target.checked)} />
            <span>Mark uploads as altered or synthetic content<span className="block text-[11px] text-ink-soft dark:text-mist/50">YouTube asks for this disclosure on realistic AI voices. Sent with every upload of the series.</span></span>
          </label>
          <label className="flex items-start gap-2 text-sm text-ink dark:text-mist">
            <input type="checkbox" className="mt-1" checked={numbered} onChange={(e) => setNumbered(e.target.checked)} />
            <span>Numbered titles<span className="block text-[11px] text-ink-soft dark:text-mist/50">Prompts builds “(5/5) Heading | Subtitle” and, for a deep dive, “(5.1/5) Heading | Topic — deep dive” with the heading of its published siblings or parent as they are on YouTube. Off: the model writes the whole title.</span></span>
          </label>
          <label className="block space-y-1">
            <span className={label}>Notice at the end of every description</span>
            <textarea id={`pub-disclaimer-${series.id}`} className="w-full min-h-[4rem] rounded-lg p-2 text-sm bg-white border border-parchment-border text-ink dark:bg-midnight-700 dark:border-midnight-600 dark:text-mist" placeholder="e.g. Toto video slouží ke vzdělávání a nenahrazuje odbornou lékařskou péči." value={disclaimer} onChange={(e) => setDisclaimer(e.target.value)} />
          </label>
          {save.error && <p className="text-sm text-red-600">{save.error.message}</p>}
          <button className={btn} disabled={!dirty || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Save</button>
        </div>
      )}
    </section>
  )
}
